<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Input, Button, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getErrorOrInfoMessageForMinAndMaxUnit } from 'src/helpers/utilities';
	import { getTemplateService } from 'src/helpers/xt1_helpers/template_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';

	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device: Device;

	let waitForResponse = false;
	let targetLock = false;
	let value: number[] = [];
	let min = 5;
	let max = 50;

	const unit: 'C' | 'F' = (getTemplateService(device)?.settings?.temp_unit || 'C').toUpperCase();

	const onSettingChange = (device: Device) => {
		const _value: number[] = getTemplateService(device)?.settings?.temp_range || [0, 0];
		min = unit == 'C' ? 5 : 41;
		max = unit == 'C' ? 50 : 122;
		if (!waitForResponse && !targetLock && value !== _value) {
			value = _value;
		}
	};

	const saveValue = async () => {
		waitForResponse = true;
		const _value = value;
		const res: SettingRequest = await gen2Request(device, 'Service.SetConfig', {
			id: 0,
			config: {
				temp_range: _value,
			},
		});
		if (res?.isok) {
			value = _value;
			targetLock = false;
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	$: minInfo = getErrorOrInfoMessageForMinAndMaxUnit(
		value[0],
		min,
		Math.max(value[1] - 5, min + 5),
		`°${unit}`,
	);
	$: maxInfo = getErrorOrInfoMessageForMinAndMaxUnit(
		value[1],
		Math.max(value[0] + 5, min),
		max,
		`°${unit}`,
	);
	$: onSettingChange(device);
	onSettingChange(device);

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: saveValue,
				loading: waitForResponse,
				disabled: waitForResponse,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Text class="mb-s text-bold"
	>{$format('set_minimal_temperature', {
		default: 'Set minimal temperature',
	})}</Text
>

<Input
	aria-label={$format('input_min_temperature', { default: 'Minimum Temperature' })}
	class="mb-m"
	size="m"
	icon="far fa-thermometer-three-quarters"
	value={value[0].toString()}
	info={minInfo.info}
	error={minInfo.error}
	on:input={(e) => {
		targetLock = true;
		value[0] = Number(e.target['value']);
	}}
	id="input-60gnxf3"
/>

<Text class="mb-s text-bold"
	>{$format('set_maximum_temperature', {
		default: 'Set maximum temperature',
	})}</Text
>

<Input
	aria-label={$format('input_max_temperature', { default: 'Maximum Temperature' })}
	class="mb-m"
	size="m"
	icon="far fa-thermometer-three-quarters"
	value={value[1].toString()}
	info={maxInfo.info}
	error={maxInfo.error}
	on:input={(e) => {
		targetLock = true;
		value[1] = Number(e.target['value']);
	}}
	id="input-mpafqz0"
/>

<Text class="mb-m text-warning" styleType="tiny"
	>{$format('text_device_drawer_max_greater_than_min', {
		default: '* Max temperature must be greater than min temperature by at least 5 degrees.',
	})}</Text
>

{#if !$showNewLayout}
	<Button loading={waitForResponse} size="m" width="full" on:click={saveValue}
		>{$format('button_save')}</Button
	>
{/if}
